import { staticPageMetadata } from '../../lib/i18n/staticPageMetadata';
import type { ScrollViewInstance, ViewInstance } from 'react-native';
import { View, Text, StyleSheet, ScrollView, Platform, TouchableOpacity, ActivityIndicator } from 'react-native';
import { Stack, Link } from 'expo-router';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import MetaTags from '../../components/MetaTags';
import { useState, useEffect, useRef } from 'react';
import { supabase, toError } from '../../lib/supabase';
import type { Database } from '../../lib/supabase';
import colors from '../../lib/colors';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import {
    resolveMessage,
    type LocalizedMessage,
    useOptionalResolvedMessage,
} from '../../lib/i18n/messages';
import { overlayRuntimeTaxonomyHierarchy } from '../../lib/i18n/taxonomy';
import { useAppLocale } from '../../lib/i18n/AppI18nProvider';

// Import the static domains hierarchy
let staticDomainsHierarchy: any[] = [];
try {
    staticDomainsHierarchy = overlayRuntimeTaxonomyHierarchy(
        require('../../assets/domains-hierarchy.json')
    );
    console.log('📄 Loaded static domains hierarchy:', staticDomainsHierarchy.length);
} catch (e) {
    console.log('📄 No static domains hierarchy file found');
}

type Domain = Database['public']['Tables']['domains']['Row'] & {
    fields: Field[];
};
type Field = Database['public']['Tables']['fields']['Row'];

export default function FieldsScreen() {
    const { i18n } = useLingui();
    const { messageLocale } = useAppLocale();
    // Initialize with static data
    const [domains, setDomains] = useState<Domain[]>(() => (
        overlayRuntimeTaxonomyHierarchy(staticDomainsHierarchy, messageLocale) as Domain[]
    ));
    const [loading, setLoading] = useState(false); // Start false since we have static data
    const [error, setError] = useState<LocalizedMessage | null>(null);
    const resolvedError = useOptionalResolvedMessage(error);
    const scrollViewRef = useRef<ScrollViewInstance>(null);
    const domainRefs = useRef<{ [key: string]: ViewInstance | null }>({});

    useEffect(() => {
        // Only fetch if we're on the client and don't have static data
        if (Platform.OS === 'web' && typeof window !== 'undefined' && staticDomainsHierarchy.length === 0) {
            fetchDomainsHierarchy();
        }
    }, []);

    const fetchDomainsHierarchy = async () => {
        setLoading(true);
        try {
            // Fetch domains
            const { data: domainsData, error: domainsError } = await supabase
                .from('domains')
                .select('*')
                .order('name', { ascending: true });

            if (domainsError) throw toError(domainsError);

            // Fetch fields
            const { data: fieldsData, error: fieldsError } = await supabase
                .from('fields')
                .select('*')
                .order('name', { ascending: true });

            if (fieldsError) throw toError(fieldsError);

            // Build hierarchy
            const hierarchy = (domainsData || []).map(domain => ({
                ...domain,
                fields: (fieldsData || []).filter(field => field.domain_id === domain.id)
            }));

            setDomains(overlayRuntimeTaxonomyHierarchy(
                hierarchy,
                messageLocale
            ) as Domain[]);
        } catch (err: any) {
            console.error('Error fetching domains hierarchy:', err);
            setError(msg({
                id: 'taxonomy.fields.loadError',
                message: 'Failed to load fields',
            }));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setDomains((current) => (
            overlayRuntimeTaxonomyHierarchy(current, messageLocale) as Domain[]
        ));
    }, [messageLocale]);

    const scrollToDomain = (domainId: number) => {
        const domainRef = domainRefs.current[String(domainId)];
        if (domainRef && scrollViewRef.current) {
            domainRef.measureLayout(
                scrollViewRef.current as any,
                (x, y) => {
                    scrollViewRef.current?.scrollTo({ y: y - 20, animated: true });
                },
                () => {}
            );
        }
    };

    const healthDomain = domains.find((domain) => domain.slug === 'health-sciences');
    const lifeDomain = domains.find((domain) => domain.slug === 'life-sciences');
    const physicalDomain = domains.find((domain) => domain.slug === 'physical-sciences');
    const socialDomain = domains.find((domain) => domain.slug === 'social-sciences');

    return (
        <>
            <Stack.Screen options={{ headerShown: false }} />
            <View style={styles.page}>
                <MetaTags
                    surfaceId="fields"
                    title={msg({
                        id: 'seo.fields.title',
                        message: 'Paperzilla Research Topics. Discover academic papers with AI-powered analysis',
                    })}
                    description={msg({
                        id: 'seo.fields.description',
                        message: 'Browse research Fields. Discover academic papers with AI-powered analysis',
                    })}
                />
                <Header />
                <ScrollView ref={scrollViewRef} style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} showsVerticalScrollIndicator={false}>
                    <View style={styles.contentContainer}>
                        <Link href="/summarize" asChild>
                            <TouchableOpacity style={styles.backLink}>
                                <Text style={styles.backLinkText}>
                                    <Trans id="taxonomy.fields.back">← Back</Trans>
                                </Text>
                            </TouchableOpacity>
                        </Link>

                        <Text 
                            style={styles.title}
                            accessibilityRole="header"
                            {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 1 })}
                        >
                            <Trans id="taxonomy.fields.title">Paperzilla Research Topics</Trans>
                        </Text>
                        <Text style={styles.subtitle}>
                            <Trans id="taxonomy.fields.hierarchyExplanation" comment="The four linked values are product-owned scientific domain names. OpenAlex is a provider name.">
                                <Text style={{ fontWeight: 'bold' }}>How our browsing hierarchy works:</Text> Paperzilla groups every paper into one of four broad scientific domains (
                                <Text style={styles.domainLink} onPress={() => healthDomain && scrollToDomain(healthDomain.id)}>{healthDomain?.name ?? ''}</Text>
                                {', '}
                                <Text style={styles.domainLink} onPress={() => lifeDomain && scrollToDomain(lifeDomain.id)}>{lifeDomain?.name ?? ''}</Text>
                                {', '}
                                <Text style={styles.domainLink} onPress={() => physicalDomain && scrollToDomain(physicalDomain.id)}>{physicalDomain?.name ?? ''}</Text>
                                {', and '}
                                <Text style={styles.domainLink} onPress={() => socialDomain && scrollToDomain(socialDomain.id)}>{socialDomain?.name ?? ''}</Text>
                                ) and then into 5-10 finer-grained research fields inside each domain. The structure mirrors the OpenAlex ontology, but we refine it with AI-driven citation networks so related work surfaces side-by-side. Browsing this hierarchy lets you jump straight from a discipline overview to seminal papers - even when exact keywords differ - making research-paper discovery faster and more serendipitous.
                            </Trans>
                        </Text>

                        {loading && domains.length === 0 ? (
                            <View style={styles.loadingContainer}>
                                <ActivityIndicator size="large" color={colors.primaryGreen} />
                            </View>
                        ) : resolvedError ? (
                            <View style={styles.errorContainer}>
                                <Text style={styles.errorText}>{resolvedError}</Text>
                            </View>
                        ) : (
                            <View style={styles.domainsContainer}>
                                {domains.map((domain) => (
                                    <View 
                                        key={domain.id} 
                                        style={styles.domainSection}
                                        ref={(ref) => { domainRefs.current[String(domain.id)] = ref; }}
                                        collapsable={false}
                                    >
                                        <View style={styles.domainHeaderContainer}>
                                            <Text 
                                                style={styles.domainHeader}
                                                accessibilityRole="header"
                                                {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 2 })}
                                            >
                                                {domain.name}
                                            </Text>
                                            {domain.works_count !== null && domain.works_count > 0 && (
                                                <View style={styles.domainBadge}>
                                                    <Text style={styles.domainBadgeText}>
                                                        {resolveMessage(i18n, msg({
                                                            id: 'taxonomy.paperCount',
                                                            message: '{count, plural, one {# paper} other {# papers}}',
                                                        }), { count: domain.works_count })}
                                                    </Text>
                                                </View>
                                            )}
                                        </View>
                                        {domain.description && (
                                            <Text style={styles.domainDescription}>{domain.description}</Text>
                                        )}
                                        <View style={styles.fieldsGrid}>
                                            {domain.fields.map((field) => (
                                                <Link
                                                    key={field.id}
                                                    href={`/field/${field.id}/${field.slug}`}
                                                    asChild
                                                >
                                                    <TouchableOpacity
                                                        style={styles.fieldCard}
                                                    >
                                                        <View style={styles.fieldCardHeader}>
                                                            <Text 
                                                                style={styles.fieldName}
                                                                accessibilityRole="header"
                                                                {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 3 })}
                                                            >
                                                                {field.name}
                                                            </Text>
                                                            {field.works_count !== null && field.works_count > 0 && (
                                                                <View style={styles.fieldBadge}>
                                                                    <Text style={styles.fieldBadgeText}>
                                                                        {resolveMessage(i18n, msg({
                                                                            id: 'taxonomy.paperCount',
                                                                            message: '{count, plural, one {# paper} other {# papers}}',
                                                                        }), { count: field.works_count })}
                                                                    </Text>
                                                                </View>
                                                            )}
                                                        </View>
                                                        <Text style={styles.fieldDescription} numberOfLines={3}>
                                                            {field.description}
                                                        </Text>
                                                    </TouchableOpacity>
                                                </Link>
                                            ))}
                                        </View>
                                    </View>
                                ))}
                            </View>
                        )}
                    </View>
                    <Footer />
                </ScrollView>
            </View>
        </>
    );
}

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: '#fafafa',
    },
    contentContainer: {
        width: '100%',
        maxWidth: 900,
        alignSelf: 'center',
        paddingHorizontal: 20,
        paddingTop: 20,
        paddingBottom: 40,
        flexGrow: 1,
    },
    scrollView: {
        flex: 1,
    },
    scrollViewContent: {
        flexGrow: 1,
    },
    backLink: {
        marginBottom: 24,
    },
    backLinkText: {
        fontSize: 16,
        color: colors.primaryGreen,
        fontWeight: '500',
    },
    title: {
        fontSize: 28,
        fontWeight: 'bold',
        color: colors.primaryGreenDark,
        marginBottom: 12,
    },
    subtitle: {
        fontSize: 16,
        color: '#4b5563',
        lineHeight: 26,
        marginBottom: 40,
    },
    loadingContainer: {
        paddingVertical: 60,
        alignItems: 'center',
    },
    errorContainer: {
        backgroundColor: '#fef2f2',
        borderWidth: 1,
        borderColor: '#fecaca',
        borderRadius: 8,
        padding: 20,
        marginBottom: 20,
    },
    errorText: {
        color: '#dc2626',
        fontSize: 16,
        textAlign: 'center',
    },
    domainsContainer: {
        marginBottom: 40,
    },
    domainSection: {
        marginBottom: 36,
    },
    domainHeaderContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 8,
        paddingBottom: 8,
        borderBottomWidth: 2,
        borderBottomColor: colors.veryLightGreen,
    },
    domainHeader: {
        fontSize: 24,
        fontWeight: '700',
        color: colors.primaryGreenDark,
        flex: 1,
    },
    domainBadge: {
        backgroundColor: '#f9fafb',
        borderRadius: 6,
        paddingHorizontal: 8,
        paddingVertical: 3,
        marginLeft: 12,
        borderWidth: 1,
        borderColor: '#e5e7eb',
    },
    domainBadgeText: {
        fontSize: 12,
        color: '#9ca3af',
        fontWeight: '600',
    },
    domainDescription: {
        fontSize: 16,
        color: '#6b7280',
        lineHeight: 24,
        marginBottom: 16,
    },
    fieldsGrid: {
        marginTop: 12,
    },
    fieldCard: {
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 20,
        marginBottom: 16,
        borderWidth: 1,
        borderColor: '#e5e7eb',
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'all 0.2s',
            ':hover': {
                borderColor: colors.primaryGreen,
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            },
        }),
    },
    fieldCardHeader: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: 8,
    },
    fieldName: {
        fontSize: 20,
        fontWeight: '600',
        color: colors.primaryGreenDark,
        flex: 1,
        paddingRight: 8,
    },
    fieldDescription: {
        fontSize: 14,
        color: '#6b7280',
        lineHeight: 20,
    },
    fieldBadge: {
        backgroundColor: '#f9fafb',
        borderRadius: 6,
        paddingHorizontal: 6,
        paddingVertical: 2,
        marginLeft: 8,
        borderWidth: 1,
        borderColor: '#e5e7eb',
    },
    fieldBadgeText: {
        fontSize: 11,
        color: '#9ca3af',
        fontWeight: '600',
    },
    domainLink: {
        color: colors.primaryGreen,
        textDecorationLine: 'underline',
        fontWeight: '500',
    },
});

export function generateMetadata() {
    return staticPageMetadata({
        surfaceId: 'fields',
        title: msg({ id: 'seo.fields.title', message: 'Paperzilla Research Topics. Discover academic papers with AI-powered analysis' }),
        description: msg({ id: 'seo.fields.description', message: 'Browse research Fields. Discover academic papers with AI-powered analysis' }),
    });
}
